// GS1 General Specifications §7.8: the FNC1 character is transmitted as ASCII 29
// (the "Group Separator" control character) by keyboard wedge scanners to delimit
// variable-length Application Identifiers within a single symbol.
const FNC1_CHAR_CODE = 29;

// A valid GS1 string must contain at least a 3-character symbology identifier
// (e.g. "]C1") plus at least one character of payload data.
const MIN_BARCODE_LENGTH = 4;

// All GS1 Application Identifiers (AIs) in GS1-128 and GS1 DataMatrix are exactly
// 2 decimal digits as defined in GS1 General Specifications §3.2.
const AI_PREFIX_LENGTH = 2;

// GS1 date elements (AIs 11, 12, 13, 15, 16, 17) are always encoded as YYMMDD —
// exactly 6 digits per GS1 General Specifications §3.4.
const GS1_DATE_LENGTH = 6;

// GS1 encodes years as 2-digit YY. The spec assumes the century is 20xx for all
// dates used in practice; pharmaceutical products universally expire before 2100.
const GS1_YEAR_BASE = 2000;

// GS1 Application Identifier data lengths for fixed-length AIs (GS1 General
// Specifications Appendix A, Table of AIs). Fixed-length AIs do NOT need an FNC1
// terminator because their length is known from the AI code alone.
const FIXED_LENGTH_AIS = {
  '00': 18, // SSCC (Serial Shipping Container Code)
  '01': 14, // GTIN (Global Trade Item Number)
  '02': 14, // GTIN of contained trade items
  '11': 6,  // Production date (YYMMDD)
  '12': 6,  // Due date (YYMMDD)
  '13': 6,  // Packaging date (YYMMDD)
  '15': 6,  // Best before date (YYMMDD)
  '16': 6,  // Sell by date (YYMMDD)
  '17': 6,  // Expiry date (YYMMDD)
  '20': 2,  // Internal product variant
};

// Variable-length AIs that we extract. These must be terminated by an FNC1
// character unless they appear last in the symbol (GS1 General Specifications §7.8.4).
const VARIABLE_AIS = new Set([
  '10', // Batch/lot number (up to 20 alphanumeric chars)
  '21', // Serial number (up to 20 alphanumeric chars)
]);

// Symbology identifiers prepended by keyboard wedge scanners to indicate the
// barcode type, per ISO/IEC 15424. Their presence is the primary signal that
// a scan contains GS1-structured data.
const SYMBOLOGY_IDS = [
  ']C1', // GS1-128 (most common on pharmaceutical cartons)
  ']C2', // GS1-128 with FNC1 in first position
  ']d2', // GS1 DataMatrix (common on unit-dose packaging per DSCSA)
  ']Q3', // GS1 QR Code
  ']e0', // GS1 EAN / UCC
];

/**
 * Parses a raw barcode string from a keyboard wedge scanner.
 * Returns extracted GS1 fields if the scan is a GS1-128 or GS1 DataMatrix barcode.
 *
 * @param {string} rawBarcode
 * @returns {{ isGS1: false } | { isGS1: true, gtin?: string, lotNumber?: string, expiryDate?: string }}
 */
export function parseGS1(rawBarcode) {
  console.log('[GS1] raw input:', JSON.stringify(rawBarcode), `(${rawBarcode?.length} chars)`);
  if (!rawBarcode || rawBarcode.length < MIN_BARCODE_LENGTH) {
    console.log('[GS1] too short, not GS1');
    return { isGS1: false };
  }

  let s = rawBarcode;
  let hadSymbologyId = false;

  for (const id of SYMBOLOGY_IDS) {
    if (s.startsWith(id)) {
      s = s.slice(id.length);
      hadSymbologyId = true;
      console.log('[GS1] symbology ID detected:', id, '→ payload:', s);
      break;
    }
  }

  // Strip a leading FNC1 if present (some scanners emit it before the first AI)
  if (s.charCodeAt(0) === FNC1_CHAR_CODE) s = s.slice(1);

  // Treat as GS1 if there was a symbology identifier or an embedded FNC1 separator.
  // Also accept bare strings (no prefix, no FNC1) when they start with a known
  // fixed-length AI and are long enough to hold at least that AI's data plus one
  // more AI — this handles scanners configured to suppress symbology identifiers.
  // Plain UPC-A/EAN-13 (≤13 chars) will never satisfy the length threshold.
  if (!hadSymbologyId && !rawBarcode.includes('\x1d')) {
    const firstAi = s.slice(0, 2);
    const minLen = 2 + (FIXED_LENGTH_AIS[firstAi] ?? 0) + 2;
    if (FIXED_LENGTH_AIS[firstAi] === undefined || s.length < minLen) {
      console.log('[GS1] no symbology ID, no FNC1, and does not start with known fixed-length AI → not GS1');
      return { isGS1: false };
    }
    console.log('[GS1] no symbology ID — attempting heuristic parse (starts with AI', firstAi, ')');
  }

  const result = { isGS1: true };
  let pos = 0;

  while (pos < s.length) {
    // Skip FNC1 separator characters between variable-length AIs
    if (s.charCodeAt(pos) === FNC1_CHAR_CODE) { pos++; continue; }

    const ai = s.slice(pos, pos + AI_PREFIX_LENGTH);

    if (FIXED_LENGTH_AIS[ai] !== undefined) {
      const len = FIXED_LENGTH_AIS[ai];
      const value = s.slice(pos + AI_PREFIX_LENGTH, pos + AI_PREFIX_LENGTH + len);
      console.log('[GS1] fixed AI', ai, '→', value);
      if (value.length === len) {
        if (ai === '01') result.gtin = value;
        else if (ai === '17') result.expiryDate = parseGS1Date(value);
        // AI 15 (best-before) is only used if no expiry date (AI 17) was found
        else if (ai === '15' && !result.expiryDate) result.expiryDate = parseGS1Date(value);
      }
      pos += AI_PREFIX_LENGTH + len;
    } else if (VARIABLE_AIS.has(ai)) {
      const fnc1 = s.indexOf('\x1d', pos + AI_PREFIX_LENGTH);
      const end = fnc1 === -1 ? s.length : fnc1;
      const value = s.slice(pos + AI_PREFIX_LENGTH, end);
      console.log('[GS1] variable AI', ai, '→', value);
      if (ai === '10') result.lotNumber = value;
      pos = fnc1 === -1 ? end : end + 1;
    } else {
      console.log('[GS1] unknown AI', ai, 'at pos', pos, '— stopping');
      break; // Unknown AI — cannot reliably determine field length, stop parsing
    }
  }

  // A GTIN is required for the result to be actionable
  if (!result.gtin) {
    console.log('[GS1] no GTIN found → not GS1');
    return { isGS1: false };
  }

  console.log('[GS1] result:', result);
  return result;
}

/**
 * Converts a GS1 date string (YYMMDD) to an ISO date string (YYYY-MM-DD).
 * DD=00 means last day of the month — allowed by GS1 General Specifications §3.4.3
 * and commonly used on pharmaceutical packaging to avoid mid-month expiry ambiguity.
 */
function parseGS1Date(yymmdd) {
  if (!yymmdd || yymmdd.length !== GS1_DATE_LENGTH) return undefined;
  const yy = parseInt(yymmdd.slice(0, 2), 10);
  const mm = parseInt(yymmdd.slice(2, 4), 10);
  const dd = parseInt(yymmdd.slice(4, 6), 10);
  if (isNaN(yy) || isNaN(mm) || mm < 1 || mm > 12) return undefined;
  const year = yy + GS1_YEAR_BASE;
  // new Date(year, mm, 0) gives the last day of month mm (month is 1-based here)
  const day = dd === 0 ? new Date(year, mm, 0).getDate() : dd;
  return `${year}-${String(mm).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
